export const en = {
  nav: {
    home: "Home",
    adult: "Adult",
    kids: "Kids",
    radar: "Radar",
    partners: "Partners",
    scheduleTrial: "Schedule a trial class",
  },
  footer: {
    tagline: "Leadership is not improvised, it is trained.",
    copyright: "© 2026 Vagner Lima",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    dataDeletion: "Data Deletion",
  },
  contactBanner: {
    kids: {
      eyebrow: "TAKE THE FIRST STEP",
      title: "Leadership begins in childhood.",
      description: "Talk to the professor and discover how Jiu-Jitsu can empower your child's character and confidence.",
      cta: "Schedule a trial class for my child",
    },
    adult: {
      eyebrow: "EXPERIENCE IT",
      title: "Your next strategic decision starts on the mat.",
      description: "Discover an executive experience connecting tactical mat situations to high-stakes leadership decisions.",
      cta: "Talk about private classes and experiences",
    },
  },
  home: {
    hero: {
      eyebrow: "ONE METHODOLOGY • TWO JOURNEYS",
      title: "Leadership is not improvised, it is trained.",
      subtitle: "Jiu-Jitsu, leadership, and the corporate world united in a proprietary methodology for executives, teams, and children.",
      chooseMethod: "Choose your methodology",
      howToParticipate: "How to participate →",
      photoLabel: "PROFESSOR • BLACK BELT",
    },
    journeys: {
      eyebrow: "CHOOSE YOUR PATH",
      title: "Two journeys. The same purpose.",
      subtitle: "Select the methodology you would like to explore.",
      adult: {
        tag: "FOR ADULTS, LEADERS & ENTREPRENEURS",
        title: "Executive Jiu-Jitsu",
        desc: "Strategy, self-control, and high-pressure decision making.",
        cta: "EXPLORE ADULT METHODOLOGY →",
      },
      kids: {
        tag: "FOR CHILDREN & THEIR FAMILIES",
        title: "Executive Jiu-Jitsu Kids",
        desc: "Respect, confidence, and foundational leadership from childhood.",
        cta: "EXPLORE KIDS METHODOLOGY →",
      },
    },
    bridge: {
      eyebrow: "JIU-JITSU + CORPORATE WORLD",
      title: "What happens on the mat does not end on the mat.",
      p1: "Pressure, strategy, adaptation, emotional composure, and decisive action are essential in Jiu-Jitsu as well as in executive leadership.",
      p2: "Executive Jiu-Jitsu translates situations experienced during training into actionable insights for business, family, and life.",
      step1: "01 EXPERIENCE",
      step2: "02 UNDERSTAND",
      step3: "03 APPLY",
    },
    participation: {
      eyebrow: "FIND THE RIGHT EXPERIENCE",
      title: "How to participate. Choose your path.",
      subtitle: "A tailored format for every goal, always under the direct mentorship of Professor Vagner Lima.",
      card1: {
        tag: "01 · INDIVIDUAL",
        title: "Private classes",
        desc: "A personalized experience for executives seeking technical development, strategic insight, and evolution at their own pace.",
        cta: "CHECK AVAILABILITY →",
      },
      card2: {
        tag: "02 · LEADERSHIP",
        title: "Executive cohorts",
        desc: "Immersive group sessions connecting Jiu-Jitsu principles to the challenges of leaders and entrepreneurs.",
        cta: "DISCOVER THE PROGRAM →",
      },
      card3: {
        tag: "03 · CORPORATE",
        title: "Corporate workshops",
        desc: "Bring your executive team a practical experience on leadership, communication, pressure management, and decision making.",
        cta: "DISCUSS CORPORATE SESSIONS →",
      },
      card4: {
        tag: "04 · CHILDHOOD",
        title: "Kids classes",
        desc: "Discipline, respect, confidence, and responsibility in a curriculum designed for character formation in children.",
        cta: "EXPLORE EXECUTIVE KIDS →",
      },
      schedule: {
        adultTitle: "ADULT",
        adultTime: "Monday, Wednesday & Friday · 7:00 PM & 9:15 PM",
        kidsTitle: "KIDS",
        kidsTime: "Monday, Wednesday & Friday · 10:00 AM & 2:00 PM",
        locationTitle: "LOCATION",
        locationAddress: "CT Haseyo · Rua Francisco Jacinto de Melo, 1277",
      },
    },
    beyond: {
      eyebrow: "BEYOND THE MAT",
      title: "Learning continues beyond the training session.",
      subtitle: "The experience directly connects with the daily realities of leadership and decision making.",
      feature1Title: "Leadership challenges",
      feature1Desc: "Insights applied directly to professional and personal routines.",
      feature2Title: "Continuous evolution",
      feature2Desc: "Consistency, technical milestones, and individual growth.",
      feature3Title: "Executive Radar",
      feature3Desc: "Market trends, macroeconomic intelligence, and corporate strategy.",
      cta: "Access Executive Radar →",
    },
    radarPreview: {
      eyebrow: "INTELLIGENCE FOR DECISION MAKERS",
      title: "Executive Radar. Actionable market intelligence.",
      subtitle: "Curated insights connecting macroeconomic shifts to the strategic decisions of business leaders.",
      card1Tag: "ECONOMY",
      card1Title: "Economic landscape",
      card1Desc: "Movements influencing capital costs, demand, credit, and forecasting.",
      card2Tag: "FINANCE",
      card2Title: "Financial decisions",
      card2Desc: "Relevant insights for treasury management, investments, and risk mitigation.",
      card3Tag: "LEADERSHIP",
      card3Title: "Executive perspective",
      card3Desc: "The practical takeaway: how does this impact your organization and next moves?",
      cta: "Explore Executive Radar →",
    },
    partnersPreview: {
      eyebrow: "STRATEGIC CONNECTIONS",
      title: "Strategic partnerships. Building enterprise value together.",
      subtitle: "A trusted network of companies and professionals connected to leadership, finance, and wealth planning.",
      cta: "Meet our strategic partners",
      cardTag: "EXECUTIVE PARTNER · WEALTH & CONSORTIA",
      cardDesc: "Structured planning for real estate, vehicle acquisitions, and long-term equity growth.",
      cardCta: "EXPLORE PARTNERSHIP →",
    },
    professor: {
      eyebrow: "WHO LEADS THE EXPERIENCE",
      name: "Vagner Lima",
      subtitle: "From capital markets to the mat.",
      desc: "Black belt Jiu-Jitsu professor and financial market veteran with over 14 years of executive banking experience, Vagner merges strategic acumen, human development, and tactical mastery into a signature methodology.",
      badge1Num: "14+",
      badge1Text: "YEARS IN CAPITAL MARKETS",
      badge2Num: "BLACK BELT",
      badge2Text: "JIU-JITSU PROFESSOR",
      quote: "“The mat translates core principles into transformative life experiences.”",
      cta: "Speak with the professor",
    },
  },
  adult: {
    hero: {
      eyebrow: "ADULT METHODOLOGY",
      title: "The mat teaches. Leadership transforms.",
      subtitle: "An executive discipline connecting Jiu-Jitsu principles to the real challenges of leaders, founders, and decision-makers under pressure.",
      cta: "Explore the methodology",
    },
    editorial: {
      eyebrow: "EXECUTIVE JIU-JITSU",
      title: "It's not about training fighters. It's about forging leaders.",
      p1: "In the corporate arena, critical decisions rarely arrive under ideal circumstances. There is pressure, uncertainty, risk, and scarce time.",
      p2: "On the mat, students experience scenarios that foster emotional composure, situational awareness, tactical adaptation, and decisive action — competencies that transcend sports.",
      step1Title: "Experience",
      step1Desc: "The strategic principle is experienced in a real, controlled sparring situation.",
      step2Title: "Reflection",
      step2Desc: "The student observes their reaction, decision patterns, and outcomes.",
      step3Title: "Application",
      step3Desc: "The insight is mapped directly onto business and leadership challenges.",
    },
    corporatePhoto: {
      eyebrow: "FROM THE BOARDROOM TO THE MAT",
      title: "Market experience. Authentic Jiu-Jitsu mastery.",
      p1: "Executive Jiu-Jitsu is born from the convergence of two environments requiring rigorous preparation: high-stakes business and the martial arts mat.",
      p2: "Targets, negotiation, risk, composure, and relationships meet discipline, leverage, adaptation, and emotional resilience.",
      quote: "“It's not about turning executives into fighters. It's about training leaders.”",
    },
    principles: {
      eyebrow: "THE CORE METHODOLOGY",
      title: "10 principles. A sharper, more resilient leadership.",
      viewMore: "EXPLORE THE REMAINING FIVE PRINCIPLES",
      items: [
        { num: "01", title: "Stability", desc: "Build a solid base to make decisions with confidence and balance." },
        { num: "02", title: "Adaptation", desc: "Identify strategic leverage even in unfavorable circumstances." },
        { num: "03", title: "Connection", desc: "Strengthen active listening, rapport, and positive influence." },
        { num: "04", title: "Strategy", desc: "Transform motion into progress with methodical clarity." },
        { num: "05", title: "Control", desc: "Consolidate your position before accelerating action." },
        { num: "06", title: "Sustainment", desc: "Maintain long-term outcomes through consistency and poise." },
        { num: "07", title: "Reaction", desc: "Respond to friction with intelligence, composure, and speed." },
        { num: "08", title: "Pressure", desc: "Navigate high-stress situations with purpose and self-control." },
        { num: "09", title: "Opportunity", desc: "Recognize openings created through deliberate preparation." },
        { num: "10", title: "Execution", desc: "Convert sound decisions into tangible, measurable results." },
      ],
    },
    benefits: {
      eyebrow: "WHO IT IS FOR",
      title: "For those who lead people, enterprises, and decisions.",
      audiences: [
        "Founders and entrepreneurs",
        "C-level executives and managers",
        "High-performance professionals",
        "Adults committed to personal mastery",
      ],
    },
    formats: {
      eyebrow: "WAYS TO PARTICIPATE",
      title: "One methodology. Tailored formats.",
      card1Title: "Private 1-on-1 sessions",
      card1Desc: "Exclusive instruction and technical progress tailored to your schedule and goals.",
      card2Title: "Executive cohorts",
      card2Desc: "Jiu-Jitsu and leadership immersion alongside fellow business leaders.",
      card3Title: "Corporate experiences",
      card3Desc: "Workshops designed for management teams and leadership development.",
      cta: "Find the best experience for me",
    },
    ecosystem: {
      eyebrow: "A COMPREHENSIVE GROWTH ECOSYSTEM",
      title: "Train. Reflect. Continuously evolve.",
      subtitle: "The program bridges in-person mat training with leadership challenges, progress tracking, and market intelligence.",
      card1Title: "Mat Training",
      card1Desc: "Hands-on sparring, controlled pressure, and real-time decision making.",
      card2Title: "Challenges",
      card2Desc: "Translating mat insights into boardroom and operational routines.",
      card3Title: "Evolution",
      card3Desc: "Objective tracking of mat hours, techniques, and personal milestones.",
      card4Title: "Radar",
      card4Desc: "Macroeconomics, finance, and leadership analysis in context.",
    },
  },
  kids: {
    hero: {
      eyebrow: "EXECUTIVE JIU-JITSU KIDS",
      title: "Leaders on the Mat.",
      subtitle: "Far beyond learning moves: developing self-confidence, character, and core values that stay with children inside and outside the academy.",
      cta: "Explore the methodology",
      badge: "FORMING LEADERS FROM CHILDHOOD",
    },
    editorial: {
      eyebrow: "WHAT YOUR CHILD WILL DEVELOP",
      title: "Confidence begins on the mat. And empowers them for life.",
      p1: "The 'Leaders on the Mat' methodology harnesses Jiu-Jitsu as an educational tool for physical, emotional, and social development, respecting each child's age and pace.",
      p2: "Through age-appropriate challenges, we cultivate attitudes that parents observe at home, in school, and in social interactions.",
    },
    pillars: {
      eyebrow: "FIVE PILLARS OF FORMATION",
      title: "Young in age. Great in attitude.",
      items: [
        { num: "01", title: "Respect", desc: "Learn to listen, take turns, and treat others with dignity and care." },
        { num: "02", title: "Self-control", desc: "Identify emotions, calibrate force, and think before taking action." },
        { num: "03", title: "Courage", desc: "Embrace new challenges and persevere through initial difficulty." },
        { num: "04", title: "Cooperation", desc: "Support teammates, share victories, and understand group value." },
        { num: "05", title: "Responsibility", desc: "Own your actions, care for the shared space, and respect others." },
      ],
      promiseTitle: "Every child evolves at their own natural pace.",
      promiseSubtitle: "No public rankings, destructive labels, or toxic comparisons.",
    },
    family: {
      eyebrow: "WHAT PARENTS CAN EXPECT",
      title: "Character formation that goes beyond technique.",
      benefit1Title: "Greater confidence",
      benefit1Desc: "Children celebrate their own breakthroughs and face challenges with poise.",
      benefit2Title: "Emotional balance",
      benefit2Desc: "Builds the ability to manage frustration and respond with mindfulness.",
      benefit3Title: "Positive social habits",
      benefit3Desc: "Strengthens respect, empathy, and constructive behavior at school and home.",
      benefit4Title: "Family integration",
      benefit4Desc: "Mat lessons harmonize with household values and celebrate personal growth.",
    },
    schedule: {
      eyebrow: "SCHEDULE & LOCATION",
      title: "Every great milestone begins with the first step.",
      whenTitle: "DAYS",
      whenDesc: "Monday, Wednesday & Friday",
      whenHours: "10:00 AM & 2:00 PM",
      whereTitle: "WHERE",
      wherePlace: "CT Haseyo",
      whereAddress: "Rua Francisco Jacinto de Melo, 1277",
    },
  },
  radar: {
    hero: {
      eyebrow: "EXECUTIVE RADAR",
      title: "News for leaders who must decide.",
      subtitle: "Capital markets, leadership, corporate developments, and macroeconomics curated with strategic context for business leaders.",
      liveNews: "AUTOMATICALLY UPDATED NEWS FEED",
      editorialSources: "CURATED SOURCES FOR BUSINESS EXECUTIVES",
    },
    orbit: {
      mercado: "MARKETS",
      financas: "FINANCE",
      empresas: "ENTERPRISE",
      lideranca: "LEADERSHIP",
      negocios: "BUSINESS",
    },
    newsArea: {
      eyebrow: "HAPPENING NOW",
      title: "The market in motion. Your strategy too.",
      liveSubtitle: "Recent news curated from premier sources with actionable context for decision-makers.",
      editorialSubtitle: "Direct access to reputable editorial feeds monitoring critical business trends.",
      lastUpdate: "Last updated:",
      recentUpdate: "Recent update",
      selectedSources: "SELECTED SOURCES",
      newsCount: "{{count}} ARTICLES",
      sourcePrefix: "Source:",
      executiveView: "EXECUTIVE PERSPECTIVE",
      readFull: "READ FULL ARTICLE ↗",
      accessEditorial: "ACCESS SOURCE ↗",
    },
    method: {
      eyebrow: "MORE THAN RAW INFORMATION",
      title: "Read the scenario. Prepare the decision.",
      step1Title: "Fact",
      step1Desc: "Understand objectively what transpired.",
      step2Title: "Context",
      step2Desc: "Assess financial risk, business impact, and emerging opportunities.",
      step3Title: "Decision",
      step3Desc: "Bridge the insight to your company's operational and strategic reality.",
    },
    cta: {
      eyebrow: "JIU-JITSU + CORPORATE WORLD",
      title: "Composure before action. Strategy before force.",
      button: "Explore Adult Methodology",
    },
  },
  partners: {
    hero: {
      eyebrow: "EXECUTIVE JIU-JITSU NETWORK",
      title: "High-trust connections. Real business opportunities.",
      subtitle: "An ecosystem of enterprises and professionals who share core values of integrity, strategic excellence, and sustainable growth.",
      cta: "Explore our partners",
      markTag: "EXECUTIVE",
      markTitle: "Connections & Business.",
      markSubtitle: "FROM THE MAT TO THE BOARDROOM",
    },
    intro: {
      eyebrow: "STRATEGIC ALLIANCES",
      title: "More than endorsements. Alliances built on trust.",
      desc: "Executive Jiu-Jitsu connects founders, executives, families, and professionals to industry leaders that accelerate corporate, wealth, and personal objectives.",
    },
    showcase: {
      eyebrow: "EXECUTIVE PARTNERS",
      title: "Enterprises in our trusted network.",
      partnerTag: "EXECUTIVE PARTNER",
      category: "CONSORTIA · WEALTH & ASSET PLANNING",
      cardTitle: "Plan today. Acquire with strategic discipline.",
      cardDesc: "Structured solutions in financial consortia for disciplined real estate, fleet, and long-term equity accumulation.",
      tag1: "Real Estate",
      tag2: "Vehicles",
      tag3: "Wealth Planning",
      cta: "Request partner introduction",
      disclosure: "Featured partner companies are responsible for their respective terms, offerings, and advisory services. Inquire directly for details.",
    },
    connectionAreas: {
      eyebrow: "AN EXPANDING NETWORK",
      title: "Core domains connecting with your world.",
      items: [
        { num: "01", title: "Wealth planning & consortia", desc: "Strategic instruments for long-term equity growth and asset acquisition." },
        { num: "02", title: "Finance & commercial advisory", desc: "Credit structures, financial optimization, and corporate opportunities." },
        { num: "03", title: "Corporate & legal counsel", desc: "Legal, accounting, and strategic frameworks for risk-managed decisions." },
        { num: "04", title: "Health & executive performance", desc: "Specialists focused on longevity, vitality, and physical resilience." },
      ],
    },
    invitation: {
      eyebrow: "YOUR COMPANY & EXECUTIVE JIU-JITSU",
      title: "High-value connections open new horizons.",
      desc: "If your firm shares our commitment to excellence, integrity, and leadership development, let's explore a partnership.",
      cta: "Introduce my company",
    },
  },
  legal: {
    badge: "COMPLIANCE & TRANSPARENCY",
    lastUpdated: "Last updated: August 28, 2026",
    tabs: {
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      dataDeletion: "Data Deletion",
    },
    privacy: {
      title: "Privacy Policy",
      subtitle: "Our commitment to data protection, transparency, and user privacy in full compliance with LGPD (Brazilian General Data Protection Law), Google Play Console, and Apple App Store guidelines.",
      sections: [
        {
          title: "1. Data Controller Identification",
          content: "Executive Jiu-Jitsu, led and technically supervised by Professor Vagner Lima, acts as the data controller for personal data collected through our mobile applications (Google Play and Apple App Store), web platforms, and official communication channels.",
        },
        {
          title: "2. Personal Data We Collect",
          content: "We only collect data strictly necessary to provide martial arts instruction, scheduling, and community services:\n• Contact & Identification Data: Full name, email address, phone number (WhatsApp), and date of birth.\n• Profile & Training Data: Belt rank, attendance history, enrolled programs (Adult/Kids), and trial class bookings.\n• Device & Technical Data: Anonymous device identifiers, operating system version, and essential diagnostics to maintain app stability.",
        },
        {
          title: "3. Purposes and Legal Basis for Processing",
          content: "Your data is processed in accordance with LGPD (Art. 7, I & V) and international privacy frameworks:\n• Managing class reservations, check-ins, and student attendance records.\n• Communicating schedule updates, seminars, promotions, and academic milestones.\n• Providing customer support and continuously improving user experience.\n• Complying with applicable legal, accounting, and regulatory requirements.",
        },
        {
          title: "4. Data Sharing & Security",
          content: "Executive Jiu-Jitsu does not sell, rent, or monetize your personal data. We only share information with trusted infrastructure providers (secure cloud hosting, encrypted databases, and push notification relays) under strict confidentiality agreements.",
        },
        {
          title: "5. Security Safeguards",
          content: "We implement industry-standard security measures, including end-to-end TLS/HTTPS encryption, strict role-based access controls, and ongoing vulnerability monitoring to protect your information against unauthorized access or breaches.",
        },
        {
          title: "6. Your Rights as a Data Subject",
          content: "Under LGPD and international privacy standards, you have the right to:\n• Confirm processing and request access to your personal data.\n• Request correction of incomplete, inaccurate, or outdated data.\n• Withdraw previously given consent at any time.\n• Request anonymization, blocking, or permanent deletion of your data.\n• Obtain the complete deletion of your account and related records.",
        },
        {
          title: "7. Data Protection Officer (DPO) Contact",
          content: "For any inquiries, questions, or requests regarding your personal data, please contact our privacy desk directly at contato@executivejiujitsu.com or via WhatsApp at +55 (48) 99608-7396.",
        },
      ],
    },
    terms: {
      title: "Terms of Use",
      subtitle: "Standard conditions governing the use of the Executive Jiu-Jitsu mobile application, web platforms, and mat training participation.",
      sections: [
        {
          title: "1. Acceptance of Terms",
          content: "By downloading, installing, or accessing the Executive Jiu-Jitsu mobile application or online services, you acknowledge that you have read, understood, and agreed to be bound by these Terms of Use and our Privacy Policy.",
        },
        {
          title: "2. User Accounts and Security",
          content: "Users agree to provide accurate and updated information upon registering or booking classes. You are solely responsible for maintaining the confidentiality of your account credentials and preventing unauthorized access.",
        },
        {
          title: "3. Physical Readiness and Health Notice",
          content: "Brazilian Jiu-Jitsu is a physical contact martial art requiring physical exertion. You (or your parent/legal guardian if under 18) confirm that you are physically fit and healthy to participate in our sessions. We recommend obtaining prior medical clearance.",
        },
        {
          title: "4. Code of Conduct on the Mat",
          content: "We uphold mutual respect, discipline, personal safety, and integrity. Any reckless, unsportsmanlike, aggressive, or discriminatory conduct will result in immediate suspension from both the training facilities and digital platforms.",
        },
        {
          title: "5. Intellectual Property",
          content: "All trademarks, proprietary teaching methodologies, logos, digital content, text, media, and code associated with Executive Jiu-Jitsu and Professor Vagner Lima are exclusively owned and protected by copyright and intellectual property laws.",
        },
        {
          title: "6. Modifications and Account Termination",
          content: "We reserve the right to modify these terms periodically to reflect product updates or legal compliance. You may terminate your account at any time by requesting full account and data deletion.",
        },
        {
          title: "7. Governing Law and Jurisdiction",
          content: "These terms are governed by the laws of the Federative Republic of Brazil. Any disputes arising from these terms shall be submitted to the jurisdiction of the courts of Palhoça / Florianópolis, Santa Catarina, Brazil.",
        },
      ],
    },
    dataDeletion: {
      title: "Account & Data Deletion Policy",
      subtitle: "Clear, transparent instructions on how to request the permanent deletion of your account and associated personal data, adhering strictly to Google Play and Apple App Store requirements.",
      overview: "Executive Jiu-Jitsu respects your right to control your personal information. If you wish to permanently delete your account and personal records from our databases, we provide direct, hassle-free web-based options without requiring the mobile application to remain installed on your device.",
      howToTitle: "How to request permanent account deletion:",
      steps: [
        {
          num: "01",
          title: "Fill out the web form",
          desc: "Provide your full name and the registered email associated with your account.",
        },
        {
          num: "02",
          title: "Submit your request",
          desc: "Send the request directly via our automated web submission, pre-formatted email, or official WhatsApp channel.",
        },
        {
          num: "03",
          title: "Processing and Confirmation",
          desc: "Our privacy team will verify the request and permanently delete your data within a maximum of 15 business days in accordance with data protection legislation.",
        },
      ],
      tableTitle: "Data Retention and Deletion Overview:",
      tableHeaders: {
        category: "Data Category",
        action: "Deletion Treatment",
        retention: "Retention / Legal Justification",
      },
      tableRows: [
        {
          category: "Account Credentials (login, email, password)",
          action: "Permanently deleted",
          retention: "Immediately purged upon request validation.",
        },
        {
          category: "Profile Data (name, phone, belt rank, photo)",
          action: "Permanently deleted",
          retention: "Irreversibly erased from our active databases and servers.",
        },
        {
          category: "Training History & Attendance Logs",
          action: "Anonymized / Removed",
          retention: "Historical aggregate metrics stripped of any personal identifiers.",
        },
        {
          category: "Billing & Financial Transaction Receipts",
          action: "Retained under statutory requirement",
          retention: "Strictly archived under Brazilian fiscal and tax compliance regulations for 5 years, stored in a secure, restricted environment.",
        },
      ],
      form: {
        title: "Account Deletion Request Form",
        desc: "Complete the fields below to submit a formal request to permanently delete your account and associated records.",
        nameLabel: "Full Name",
        namePlaceholder: "Enter your full name",
        emailLabel: "Registered Account Email",
        emailPlaceholder: "example@email.com",
        reasonLabel: "Reason for deletion (optional)",
        reasonPlaceholder: "Let us know why you are leaving or if you experienced any issues...",
        consentLabel: "I understand that deleting my account is permanent and will remove my training history and belt progression records.",
        submitEmailBtn: "Request deletion via Email",
        submitWhatsappBtn: "Request deletion via WhatsApp",
        validationError: "Please enter your full name, a valid email address, and check the confirmation box.",
        successMsg: "Your deletion request has been prepared! If your email client did not open automatically, you can also reach us via WhatsApp using the button below.",
      },
    },
  },
};

